import { useQuery } from '@tanstack/react-query';
import { api, type PoolDetail } from '../api.js';

/**
 * Resolves the viewer's own participant id within a bolão.
 *
 * Neither `GET /groups/:id/bolaos/:poolId` nor the participant-profile
 * endpoint returns it directly — the leaderboard endpoints are the only ones
 * that list every participant tagged with `isYou`, so they're the cheapest
 * accurate source. Only queried once `pool.isPlaying` is known true, so an
 * organizer who never joined their own bolão doesn't trigger either fetch.
 */
export function useOwnParticipantId(
  groupId: string | undefined,
  poolId: string | undefined,
  pool: Pick<PoolDetail, 'sportId' | 'isPlaying'> | undefined,
): string | undefined {
  const isCustom = pool?.sportId === 'custom';
  const canQuery = !!groupId && !!poolId && !!pool?.isPlaying;

  const football = useQuery({
    queryKey: ['leaderboard', groupId, poolId],
    queryFn: () => api.getLeaderboard(groupId!, poolId!),
    enabled: canQuery && !isCustom,
  });
  const custom = useQuery({
    queryKey: ['custom-leaderboard', groupId, poolId],
    queryFn: () => api.getCustomLeaderboard(groupId!, poolId!),
    enabled: canQuery && isCustom,
  });

  const rows = isCustom ? custom.data?.rows : football.data?.rows;
  return rows?.find((row) => row.isYou)?.participantId;
}
